import { getNow } from '../../../../lib/TimeUtils';
import {
  DefaultFieldNames,
  Events,
  StatsActions,
  TTMActions,
  TTMAudioTypes,
  VoiceTrackMetricTypes,
} from '../constants';
import * as D from '../deps';
import TimeToMusicEvent from '../models/TimeToMusicEvent';

export function clearStatsEvents() {
  return {
    type: D.Constants.Analytics.ActionTypes.CLEAR_STATS_EVENTS,
  };
}

export function expireReward(rewardType, expirationTrigger) {
  return {
    type: D.Constants.Analytics.ActionTypes.EXPIRE_VALUE_EXCHANGE_REWARD,
    data: {
      rewardType,
      expirationTrigger,
    },
  };
}

export function addEvents(events, defaultEventData) {
  const actionEvents = events.map(event => {
    /* the event data overrides the default event data, for ex. page_view */
    event.eventData = Object.assign({}, defaultEventData, event.eventData);
    return event;
  });

  return {
    type: D.Constants.Analytics.ActionTypes.ADD_EVENT,
    data: {
      events: actionEvents,
    },
  };
}

export function incrementSearchSequence() {
  return {
    type: D.Constants.Analytics.ActionTypes.INCREMENT_SEARCH_SEQUENCE,
  };
}

export function resetSearchSequence() {
  return {
    type: D.Constants.Analytics.ActionTypes.RESET_SEARCH_SEQUENCE,
  };
}

export function generateBrowseSessionId() {
  return {
    type: D.Constants.Analytics.ActionTypes.GENERATE_BROWSE_SESSION_ID,
  };
}

export function logWebRegistrationEvent({ registrationEventAction }) {
  return {
    type: Events.WEB_REGISTRATION,
    data: {
      registrationEventAction,
    },
  };
}

export function logStationStarted(data) {
  return {
    type: D.Constants.Analytics.ActionTypes.STATION_STARTED,
    data,
  };
}

export function logEnterRedeemFlowForUrl() {
  return {
    type: D.Constants.Analytics.ActionTypes.ENTER_REDEEM_FLOW_FOR_URL,
  };
}

export function logBrowserAutomaticPlayback(listenerAction) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_BROWSER_AUTOPLAY_PLAYBACK,
    data: {
      action: listenerAction,
    },
  };
}

export function logPlaybackInteractions({ action, userInitiated }) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_PLAYBACK_INTERACTIONS,
    action,
    userInitiated,
  };
}

export function logAdBlockerHammerModalEvent({ reason, clicked }) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_AD_BLOCKER_HAMMER_COACHMARK,
    reason,
    clicked,
  };
}

export function logWebDisplayAdRequestEvent(data) {
  return {
    type: D.Constants.Analytics.ActionTypes.WEB_DISPLAY_AD_REQUEST,
    data,
  };
}

export function logPremiumPlaylistUnlocked(data) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_PREMIUM_PLAYLIST_UNLOCKED,
    data,
  };
}

export function logPremiumPlaylistCoachmark(reason) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_PREMIUM_PLAYLIST_COACHMARK,
    data: {
      reason,
    },
  };
}

export function logAdBlockerAudioMessageCoachmark() {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_AD_BLOCKER_AUDIO_MESSAGE_COACHMARK,
  };
}

export function logWebDisplayAdResponseEvent(data) {
  return {
    type: D.Constants.Analytics.ActionTypes.WEB_DISPLAY_AD_RESPONSE,
    data,
  };
}

export function logWebDisplayAdIasPETRequestEvent(data) {
  return {
    type: D.Constants.Analytics.ActionTypes.WEB_DISPLAY_AD_IAS_PET_REQUEST,
    data,
  };
}

export function logWebDisplayAdIasPETResponseEvent(data) {
  return {
    type: D.Constants.Analytics.ActionTypes.WEB_DISPLAY_AD_IAS_PET_RESPONSE,
    data,
  };
}

export function logWebDisplayAdStartRenderEvent(data) {
  return {
    type: D.Constants.Analytics.ActionTypes.WEB_DISPLAY_AD_START_RENDER,
    data,
  };
}

export function logWebDisplayAdFinishRenderEvent(data) {
  return {
    type: D.Constants.Analytics.ActionTypes.WEB_DISPLAY_AD_FINISH_RENDER,
    data,
  };
}

export function logWebDisplayAdDismissedEvent(data) {
  return {
    type: D.Constants.Analytics.ActionTypes.WEB_DISPLAY_AD_DISMISSED,
    data,
  };
}

export function logStationCreated({ componentName = null, stationId, createStationParams = {} }) {
  return {
    type: D.Constants.Analytics.ActionTypes.STATION_CREATED,
    data: {
      stationId,
      componentName,
      createStationParams,
    },
  };
}

export function clearLiveTracking() {
  return {
    type: D.Constants.Analytics.ActionTypes.CLEAR_LIVE_TRACKING,
  };
}

export function clearStats() {
  return {
    type: D.Constants.Analytics.ActionTypes.CLEAR_STATS,
  };
}

export function logConcertNotificationSent({
  eventId,
  artistId,
  actionType,
  actionLink,
  notificationId,
}) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_CONCERT_NOTIFICATION_SENT,
    data: {
      actionType,
      eventId,
      artistId,
      actionLink,
      notificationId,
    },
  };
}

export function logConcertNotificationViewed({
  eventId,
  artistId,
  actionType,
  actionLink,
  notificationId,
}) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_CONCERT_NOTIFICATION_VIEWED,
    data: {
      eventId,
      artistId,
      actionType,
      actionLink,
      notificationId,
    },
  };
}

export function logConcertNotificationNoLongerOnPage(eventId) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_CONCERT_NOTIFICATION_NO_LONGER_ON_PAGE,
    data: {
      eventId,
    },
  };
}

export function logConcertNotificationClicked({ eventId, artistId, actionLink, notificationId }) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_CONCERT_NOTIFICATION_CLICKED,
    data: {
      eventId,
      artistId,
      actionLink,
      notificationId,
    },
  };
}

export function logAddVariety(data) {
  const add_variety_type = D.Utils.Entity.getEntityTrackingType(data.variety_id);
  const fullData = Object.assign({}, data, { add_variety_type });
  return {
    type: D.Constants.Analytics.ActionTypes.ADD_VARIETY,
    fullData,
  };
}

export function logDeleteStation(stationId, componentName) {
  return {
    type: D.Constants.Analytics.ActionTypes.DELETE_STATION,
    data: {
      stationId,
      componentName,
    },
  };
}

export function logShuffleEdit({ stations, station_id, shuffleType }) {
  return {
    type: D.Constants.Analytics.ActionTypes.SHUFFLE_EDIT,
    data: {
      stations,
      station_id,
      type: Events.QUICK_MIX_EDIT,
      shuffle_type: shuffleType,
    },
  };
}

export function logWebViewModeEvent(pageView, viewMode) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_WEB_VIEW_MODE_EVENT,
    payload: {
      [DefaultFieldNames.PAGE_VIEW]: pageView,
      [DefaultFieldNames.VIEW_MODE]: viewMode,
    },
  };
}

export function logBackstageEvent(data) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_BACKSTAGE_EVENT,
    data,
  };
}

export function logSkipLimit() {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_SKIP_LIMIT_STATS,
  };
}

export function logTrackEndStation(reason, track) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_TRACK_END_STATION,
    data: {
      reason,
      track,
    },
  };
}

export function logTrackEnd({
  reason,
  track,
  token,
  playlistId,
  playlistType,
  duration,
  currentTime,
}) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_TRACK_END,
    data: {
      reason,
      track,
      token,
      playlistId,
      playlistType,
      duration,
      currentTime,
    },
  };
}

/*
 * Used to create stats object for audio_track_playback_event
 */
export function createAudioTrackPlaybackEvent(eventData = {}) {
  return {
    type: D.Constants.Analytics.ActionTypes.AUDIO_TRACK_PLAYBACK_STATS,
    data: eventData,
  };
}

export function logFPSEvent(eventData) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_FPS_DATA,
    data: {
      frames_per_second: eventData.fps,
    },
  };
}

export function logReplayTrackEvent(trackToken = null, eventSource = 'other') {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_REPLAY_TRACK,
    data: {
      trackToken,
      eventSource,
    },
  };
}

export function logPlayPAWelcomeAudio(sourceType) {
  return {
    type: Events.PLAYED_PA_WELCOME_AUDIO,
    sourceType,
  };
}

export function logOpenedLTUXPAUpsellCoachmark(data) {
  return {
    type: Events.OPENED_LTUX_PA_UPSELL_COACHMARK,
    data,
  };
}

export function logClickedLTUXPAUpsellCoachmark(data) {
  return {
    type: Events.CLICKED_LTUX_PA_UPSELL_COACHMARK,
    data,
  };
}

export function logClickedStartTrialPAUpsellCoachmark(data) {
  return {
    type: Events.CLICKED_START_TRIAL_PA_UPSELL_COACHMARK,
    data,
  };
}

export function logEvents(data) {
  return {
    type: D.Constants.UseCases.ActionTypes.LOG_EVENTS,
    data,
  };
}

export function logAbExperimentDiversionPoint({ experimentId, treatmentId }) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_AB_EXPERIMENT_DIVERSION_POINT,
    data: {
      experimentId,
      treatmentId,
    },
  };
}

export function logRecentlyPlayedCarouselEvent(data) {
  return {
    type: D.Constants.Analytics.ActionTypes.LOG_RECENTLY_PLAYED_CAROUSEL_EVENT,
    data,
  };
}

const voiceTrackMetricAction =
  type =>
  ({ authorId, messageId, sourceId }) => {
    return {
      type: D.Constants.Analytics.ActionTypes.LOG_VOICE_TRACK_METRIC,
      data: { authorId, messageId, sourceId, type },
    };
  };

export const logVoiceTrackAudioImpression = voiceTrackMetricAction(
  VoiceTrackMetricTypes.AUDIO_IMPRESSION,
);
export const logVoiceTrackAudioTileClick = voiceTrackMetricAction(
  VoiceTrackMetricTypes.AUDIO_TILE_CLICK,
);
export const logVoiceTrackAudioTileImpression = voiceTrackMetricAction(
  VoiceTrackMetricTypes.AUDIO_TILE_IMPRESSION,
);
export const logVoiceTrackSkip = voiceTrackMetricAction(VoiceTrackMetricTypes.SKIP);

const timeToMusicStartAction = (ttmAction, audioType) => ({
  type: StatsActions.TTM_START,
  data: TimeToMusicEvent({
    ttmAction,
    timeStart: getNow(),
    audioType,
  }),
});

export const ttmTrackSkippedTransitionFromMusic = () =>
  timeToMusicStartAction(TTMActions.TRACK_SKIPPED);
export const ttmTrackSkippedTransitionFromVideoAd = () =>
  timeToMusicStartAction(TTMActions.TRACK_SKIPPED, TTMAudioTypes.VIDEO_AD);
export const ttmTrackThumbsDownTransitionFromMusic = () =>
  timeToMusicStartAction(TTMActions.TRACK_THUMBS_DOWN);
export const ttmTrackThumbsDownTransitionFromVideoAd = () =>
  timeToMusicStartAction(TTMActions.TRACK_THUMBS_DOWN, TTMAudioTypes.VIDEO_AD);
export const ttmSourceChangedTransitionFromMusic = () =>
  timeToMusicStartAction(TTMActions.SOURCE_CHANGED, TTMAudioTypes.MUSIC);
export const ttmPlaybackResumedTransitionFromMusic = () =>
  timeToMusicStartAction(TTMActions.PLAYBACK_RESUMED, TTMAudioTypes.MUSIC);
export const ttmStillListening = () =>
  timeToMusicStartAction(TTMActions.STILL_LISTENING, TTMAudioTypes.MUSIC);
export const ttmNonAdInterruption = () =>
  timeToMusicStartAction(TTMActions.NON_AD_INTERRUPTION, TTMAudioTypes.NON_AD_INTERRUPTION);
export const ttmAudioAd = (audioType = TTMAudioTypes.MUSIC) =>
  timeToMusicStartAction(TTMActions.AUDIO_AD, audioType);
export const ttmVideoAd = () => timeToMusicStartAction(TTMActions.VIDEO_AD, TTMAudioTypes.MUSIC);
export const ttmNoUserAction = () =>
  timeToMusicStartAction(TTMActions.NO_USER_ACTION, TTMAudioTypes.MUSIC);

// Remove a single time to music event
const timeToMusicCancel = ttmAction => ({
  type: StatsActions.TTM_CANCEL,
  data: {
    ttmAction,
  },
});

export const ttmTrackSkippedCancel = () => timeToMusicCancel(TTMActions.TRACK_SKIPPED);
export const ttmTrackThumbsDownCancel = () => timeToMusicCancel(TTMActions.TRACK_THUMBS_DOWN);

// Remove all time to music events
export const timeToMusicClear = () => ({
  type: StatsActions.TTM_CLEAR,
});

const timeToMusicTransition = audioType => ({
  type: StatsActions.TTM_TRANSITION,
  data: {
    audioType,
  },
});

// Set all of the TTM events to the current time and change their audio type.
// This is useful when you want to measure ttm from the end of the ad but don't know what TTM events are queued.
export const timeToMusicTransitionFromMusic = () => timeToMusicTransition(TTMAudioTypes.MUSIC);
export const timeToMusicTransitionFromVideoAd = () => timeToMusicTransition(TTMAudioTypes.VIDEO_AD);

export function updatePreviousPlayedHarness(data) {
  return {
    type: StatsActions.UPDATE_PREVIOUS_PLAYED_HARNESS,
    data,
  };
}

export function bufferingStart() {
  return {
    type: StatsActions.BUFFERING_START,
  };
}

export function bufferingStop() {
  return {
    type: StatsActions.BUFFERING_STOP,
  };
}
